import { m } from '@aimfora/i18n'
import { Notice, SkipLink } from '@aimfora/ui'
import { Link } from '@tanstack/react-router'
import { Fragment } from 'react'
import { showCookieSettings } from '~/lib/consent'
import { LEGAL_UPDATED, type LegalSection } from '~/lib/legal'
import { parseInline } from '~/lib/legal-inline'
import type { PublicConfig } from '~/server/session.server'
import { Logo } from './logo'

const linkClass = 'underline underline-offset-2 hover:text-ink'

/** Text from the catalog with `**bold**` and `[link](href)` turned into elements. */
export function InlineText({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((part, index) => {
        // The text is fixed, so the position is a stable key
        const key = `${index}-${part.type}`
        if (part.type === 'bold') return <strong key={key}>{part.text}</strong>
        if (part.type === 'link') {
          return (
            <a
              key={key}
              className={linkClass}
              href={part.href}
              {...(part.external ? { target: '_blank', rel: 'noreferrer' } : {})}
            >
              {part.text}
            </a>
          )
        }
        return <Fragment key={key}>{part.text}</Fragment>
      })}
    </>
  )
}

/** Links to the two legal pages, for footers. */
export function LegalLinks({ className }: { className?: string }) {
  return (
    <nav aria-label={m.legal_nav_label()} className={className ?? 'flex flex-wrap gap-x-4'}>
      <Link className={linkClass} to="/terms">
        {m.legal_terms_title()}
      </Link>
      <Link className={linkClass} to="/privacy">
        {m.legal_privacy_title()}
      </Link>
      {import.meta.env.VITE_POSTHOG_KEY && (
        <button type="button" className={linkClass} onClick={() => void showCookieSettings()}>
          {m.consent_settings_link()}
        </button>
      )}
    </nav>
  )
}

const dateOf = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

/** A public page with a long text: a title, numbered-free sections, and a draft notice until the CVR is set. */
export function LegalPage({
  title,
  sections,
  company,
}: {
  title: string
  sections: LegalSection[]
  company: PublicConfig['company']
}) {
  return (
    <div className="min-h-screen bg-paper">
      <SkipLink>{m.common_skip_to_content()}</SkipLink>
      <header className="mx-auto flex h-16 max-w-3xl items-center px-4 sm:px-6">
        <Link to="/" aria-label={m.app_home_label()}>
          <Logo height={24} />
        </Link>
      </header>
      <main id="main" className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
        {company.draft && (
          <Notice tone="attention" className="mb-4" data-testid="legal-draft">
            {m.legal_draft_notice()}
          </Notice>
        )}
        <article className="rounded-md border border-line bg-surface p-6 text-[15px] leading-relaxed sm:p-8">
          <h1 className="font-semibold text-[26px] tracking-[-0.01em]">{title}</h1>
          <p className="text-ink-soft text-sm">
            {m.legal_updated({ date: dateOf(LEGAL_UPDATED) })}
          </p>
          {sections.map((section) => (
            <section key={section.id} aria-labelledby={`legal-${section.id}`}>
              <h2 id={`legal-${section.id}`} className="mt-7 mb-2 font-semibold text-[17px]">
                {section.title}
              </h2>
              {section.blocks.map((block, index) =>
                block.type === 'p' ? (
                  // The text is fixed, so the position is a stable key
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed content
                  <p key={index} className="mt-2">
                    <InlineText text={block.text} />
                  </p>
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed content
                  <ul key={index} className="mt-2 list-disc space-y-1 pl-5">
                    {block.items.map((item) => (
                      <li key={item}>
                        <InlineText text={item} />
                      </li>
                    ))}
                  </ul>
                ),
              )}
            </section>
          ))}
        </article>
        <LegalLinks className="mt-6 flex flex-wrap gap-x-4 text-ink-soft text-sm" />
      </main>
    </div>
  )
}
